import apiClient from './apiClient';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface PetOwner {
  _id: string;
  name?: string;
  email?: string;
  phone?: string;
}

export interface PetRef {
  _id: string;
  name?: string;
  title?: string;
}

export interface Pet {
  _id: string;
  title: string;
  description?: string;
  petCategory?: PetRef | string;
  breed?: PetRef | string | null;
  owner?: PetOwner | string;
  price: number;
  images?: Array<{ url?: string; image?: string } | string>;
  address?: string;
  age?: { value?: number; unit?: string } | number;
  gender?: string;
  isAvailable: boolean;
  isApproved: boolean;
  isPremium?: boolean;
  isFeatured?: boolean;
  isNegotiable?: boolean;
  viewCount?: number;
  slug?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PetsPagination {
  currentPage: number;
  totalPages: number;
  totalItems?: number;
  total?: number;
  hasMore?: boolean;
}

export interface GetPetsParams {
  page?: number;
  limit?: number;
  search?: string;
  petCategory?: string;
  isAvailable?: boolean;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

class PetsService {
  /** List pets (paginated). Backend: GET /pets → { pets, pagination }. */
  async getPets(params: GetPetsParams = {}) {
    const response = await apiClient.get<ApiResponse<{ pets: Pet[]; pagination: PetsPagination }>>(
      '/pets',
      { params },
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch pets');
    }
    // Tolerate either { pets, pagination } or a bare array in data.
    const data: any = response.data.data;
    if (Array.isArray(data)) {
      return { pets: data as Pet[], pagination: { currentPage: 1, totalPages: 1 } as PetsPagination };
    }
    return {
      pets: (data?.pets ?? []) as Pet[],
      pagination: (data?.pagination ?? { currentPage: 1, totalPages: 1 }) as PetsPagination,
    };
  }

  async getPetById(id: string) {
    const response = await apiClient.get<ApiResponse<Pet>>(`/pets/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch pet');
    }
    return response.data.data;
  }

  /** Moderation / edit. Backend: PUT /pets/:id (admin token → full field access). */
  async updatePet(id: string, data: Partial<Pet>) {
    const response = await apiClient.put<ApiResponse<Pet>>(`/pets/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update pet');
    }
    return response.data.data;
  }

  /** Toggle a boolean moderation flag (isAvailable / isApproved / isFeatured / isPremium). */
  async setPetFlag(id: string, flag: 'isAvailable' | 'isApproved' | 'isFeatured' | 'isPremium', value: boolean) {
    return this.updatePet(id, { [flag]: value } as Partial<Pet>);
  }

  async deletePet(id: string) {
    const response = await apiClient.delete<ApiResponse<null>>(`/pets/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete pet');
    }
    return true;
  }
}

const petsService = new PetsService();
export default petsService;
